import { StyleSheet, View } from "react-native";

import { useHeaderIdentity } from "@/school-identity/useHeaderIdentity";
import { makeStyles } from "@/theme/makeStyles";
import { Text } from "@/ui/Text";

import { useChromeKind } from "../chrome/useChromeKind";

import { AccountMenu } from "../AccountMenu";
import { CartButton } from "./CartButton";
import { DesktopNav } from "./DesktopNav";
import { SchoolCrest } from "./SchoolCrest";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    bar: {
      flexDirection: "row",
      alignItems: "center",
      gap: t.space.md,
      paddingHorizontal: t.space.lg,
      backgroundColor: t.color.surface,
      borderBottomWidth: t.borderWidth.card,
      borderBottomColor: t.color.border,
      // The account menu opens below the bar; keep it above the page content (2026-10-06).
      position: "relative",
      zIndex: 10,
    },
    mobile: { minHeight: t.size.touchTarget + t.space.md },
    desktop: {
      height: t.size.headerHeightDesktop,
      paddingHorizontal: t.space.x2l,
    },
    identity: {
      flexDirection: "row",
      alignItems: "center",
      gap: t.space.smd,
      flexShrink: 1,
      minWidth: 0,
    },
    name: { flexShrink: 1 },
    chip: {
      paddingHorizontal: t.space.smd,
      paddingVertical: t.space.xxs / 2,
      borderRadius: t.radius.pill,
      backgroundColor: t.color.primarySoft,
    },
    spacer: { flex: 1 },
    actions: { flexDirection: "row", alignItems: "center", gap: t.space.xs },
  }),
);

type Props = {
  /**
   * Header actions: the parent area has Cart (and the account menu on desktop); the school-admin
   * area has the account menu at every width (002, FR-024); "none" for neither.
   */
  actions?: "parent" | "account" | "none";
};

/**
 * The signed-in header (FR-005, FR-006, FR-024, FR-027; R-9; boards MHome, WebHome).
 * - `school` identity: crest, school name, optional "School admin" chip.
 * - `product` identity (no school yet): the text "Akaiva Edu Power", no image.
 * Mobile chrome: compact bar with Cart. Desktop chrome: 72 px with the section links, Cart and the
 * account button. Never shows an Akaiva logo.
 */
export function SchoolHeader({ actions = "parent" }: Props) {
  const styles = useStyles();
  const identity = useHeaderIdentity();
  const chrome = useChromeKind();
  const desktop = chrome === "desktop";

  return (
    <View
      style={[styles.bar, desktop ? styles.desktop : styles.mobile]}
      testID="school-header"
      role="banner"
    >
      <View style={styles.identity}>
        {identity.kind === "school" ? (
          <>
            <SchoolCrest school={identity.school} />
            <Text
              variant="heading"
              weight="semiBold"
              size={desktop ? "lg" : "md"}
              numberOfLines={1}
              style={styles.name}
            >
              {identity.school.name}
            </Text>
            {identity.label ? (
              <View style={styles.chip}>
                <Text size="xs" weight="semiBold" tone="primary">
                  {identity.label}
                </Text>
              </View>
            ) : null}
          </>
        ) : (
          <Text variant="heading" weight="semiBold" size="lg" numberOfLines={1}>
            Akaiva Edu Power
          </Text>
        )}
      </View>
      <View style={styles.spacer} />
      {desktop && actions === "parent" ? <DesktopNav /> : null}
      {actions === "parent" ? (
        <View style={styles.actions}>
          <CartButton />
          {desktop ? <AccountMenu /> : null}
        </View>
      ) : null}
      {actions === "account" ? (
        <View style={styles.actions}>
          <AccountMenu />
        </View>
      ) : null}
    </View>
  );
}
